import { useState, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Auth } from "aws-amplify";
import Message from "../../../components/message/message-comp";
import logo from '../../../assets/images/logo/logo.png';
import authSideImage from '../../../assets/images/others/auth-side.webp';
import AuthTextBox from "../../../components/forms/auth-text-box";
import Loader from "../../../components/form-loader/form-loader";
import { HiOutlineEye, HiOutlineEyeOff } from 'react-icons/hi';

function ForgotPasswordPage() {
    const navigate = useNavigate();
    const [auth, setAuth] = useState({
        username: '',
        verifyCode: '',
        password: '',
    });

    const [resetState, setResetState] = useState();
    const [imageFailed, setImageFailed] = useState(false);
    const [imagePreloaded, setImagePreloaded] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const [error, setError] = useState({
        username: false,
        verifyCode: false,
        password: false
    });

    const [formState, setFormState] = useState({
        processing: false,
        success: false,
        failed: false,
        message: ''
    });

    // Resend code timer
    const [resendCooldown, setResendCooldown] = useState(0);

    useEffect(() => {
        const email = auth.username.trim().toLowerCase();
        if (email) {
            const lastTime = localStorage.getItem(`lastForgotPwdRequestTime_${email}`);
            if (lastTime) {
                const elapsed = Date.now() - parseInt(lastTime, 10);
                const remaining = Math.max(0, 60 - Math.floor(elapsed / 1000));
                setResendCooldown(remaining);
            } else {
                setResendCooldown(0);
            }
        } else {
            setResendCooldown(0);
        }
    }, [auth.username]);

    useEffect(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(prev => prev - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);

    // Pre-load image for smoother rendering
    useEffect(() => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.as = 'image';
        link.href = authSideImage;
        document.head.appendChild(link);

        const img = new Image();
        img.src = authSideImage;
        img.onload = () => {
            setImagePreloaded(true);
        };
        img.onerror = () => {
            setImageFailed(true);
        };

        return () => {
            try {
                document.head.removeChild(link);
            } catch (e) {
                // ignore
            }
        };
    }, []);

    const handleChange = useCallback(event => {
        const targetName = event.target.name;
        const targetValue = event.target.value;

        if (targetName === 'username')
            auth.username = targetValue;

        if (targetName === 'verifyCode')
            auth.verifyCode = targetValue;

        if (targetName === 'password')
            auth.password = targetValue;

        setAuth(Object.assign({}, auth));
    });

    const validateForm = () => {
        let valid = true;
        if (auth.username === '') {
            error.username = true;
            valid = false;
        } else {
            error.username = false;
        }

        setError(Object.assign({}, error))
        return valid;
    }

    const validateVerifyForm = () => {
        let valid = true;
        if (auth.verifyCode === '') {
            error.verifyCode = true;
            valid = false;
        } else {
            error.verifyCode = false;
        }

        if (auth.password === '') {
            error.password = true;
            valid = false;
        } else {
            error.password = false;
        }

        setError(Object.assign({}, error))
        return valid;
    }

    const updateFormState = (processing, success, failed, message) => {
        setFormState(Object.assign({}, { processing, success, failed, message }))
    }

    const handleSubmit = useCallback(async (event) => {
        event.preventDefault();
        if (!formState.processing && validateForm()) {
            const email = auth.username.trim().toLowerCase();
            const lastTime = localStorage.getItem(`lastForgotPwdRequestTime_${email}`);
            if (lastTime) {
                const elapsed = Date.now() - parseInt(lastTime, 10);
                if (elapsed < 60000) {
                    const remaining = Math.ceil((60000 - elapsed) / 1000);
                    updateFormState(false, false, true, `Please wait ${remaining} seconds before requesting another code.`);
                    return;
                }
            }

            updateFormState(true, false, false);
            setResetState(null);
            try {
                const resp = await Auth.forgotPassword(auth.username);
                // Save time of success to localStorage
                localStorage.setItem(`lastForgotPwdRequestTime_${email}`, Date.now().toString());
                setResendCooldown(60);

                updateFormState(false, true, false);
                setResetState(resp);
            } catch (err) {
                console.error("reset pwd failed cause", err);
                updateFormState(false, false, true, err.message);
            }
        }
    });

    const handleSetNewPassword = useCallback(async (event) => {
        event.preventDefault();
        if (!formState.processing && validateVerifyForm()) {
            updateFormState(true, false, false);
            try {
                await Auth.forgotPasswordSubmit(auth.username, auth.verifyCode, auth.password);
                updateFormState(false, true, false);
                navigate('/signin');
            } catch (err) {
                console.error("reset pwd failed cause", err);
                updateFormState(false, false, true);
            }
        }
    });

    return (
        <div className="min-h-screen lg:h-screen grid grid-cols-1 lg:grid-cols-2 bg-white font-sans lg:overflow-hidden">
            <div className="flex flex-col justify-between p-6 sm:p-12 lg:p-16 overflow-y-auto h-screen lg:h-full">
                <div className="flex items-center">
                    <Link to="/" className="flex items-center gap-2.5">
                        <img
                            className="h-8 w-auto object-contain"
                            src={logo}
                            alt="BotCircuits"
                        />
                        <span className="text-xl font-bold text-gray-900 tracking-tight">BotCircuits</span>
                    </Link>
                </div>

                <div className="my-auto mx-auto w-full max-w-md py-8">
                    <div>
                        <h2 className="text-3xl font-bold text-gray-900 tracking-tight">Reset your password</h2>
                        <p className="mt-2 text-sm text-gray-600">Enter your email address to receive a verification code.</p>
                    </div>

                    {resetState &&
                        <div className="mt-4 flex justify-center items-center">
                            <Message text={`We have sent verify code to your ${resetState.CodeDeliveryDetails.Destination} email`} type="success" />
                        </div>
                    }

                    {formState.failed &&
                        <div className="mt-4 flex justify-center items-center">
                            <Message
                                text={formState.message ? formState.message : 'Reset password failed. Please check your details'}
                                type="error" />
                        </div>
                    }

                    {formState.processing &&
                        <div className="mt-4 flex justify-center items-center">
                            <Loader />
                        </div>
                    }

                    <div className="mt-6">
                        <form className="space-y-4" onSubmit={handleSubmit}>
                            <input type="hidden" name="remember" defaultValue="true" />

                             <AuthTextBox
                                id="username"
                                name="username"
                                label="Email address"
                                value={auth.username}
                                onChange={handleChange}
                                type="email"
                                autoComplete="email"
                                placeholder="you@company.com"
                                error={error.username}
                            />

                            {resetState &&
                                <AuthTextBox
                                    id="verifyCode"
                                    name="verifyCode"
                                    label="Verify Code"
                                    value={auth.verifyCode}
                                    onChange={handleChange}
                                    type="text"
                                    placeholder="Enter verification code"
                                    error={error.verifyCode}
                                />
                            }

                            {resetState &&
                                <AuthTextBox
                                    id="password"
                                    name="password"
                                    label="New Password"
                                    type={showPassword ? 'text' : 'password'}
                                    value={auth.password}
                                    onChange={handleChange}
                                    autoComplete="current-password"
                                    placeholder="Enter new password"
                                    error={error.password}
                                    suffix={
                                        showPassword ? (
                                            <HiOutlineEye size={20} className="text-gray-500 hover:text-gray-700" onClick={() => setShowPassword(false)} />
                                        ) : (
                                            <HiOutlineEyeOff size={20} className="text-gray-500 hover:text-gray-700" onClick={() => setShowPassword(true)} />
                                        )
                                    }
                                />
                            }

                            {!resetState &&
                                <div className="pt-2">
                                    <button
                                        type="submit"
                                        disabled={resendCooldown > 0}
                                        className={`flex w-full justify-center items-center rounded-lg py-3 px-4 text-sm font-semibold text-white shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-lime transition-all duration-200 uppercase tracking-wider ${resendCooldown > 0 ? 'bg-gray-400 cursor-not-allowed' : 'bg-primary hover:bg-secondary'}`}
                                    >
                                        {resendCooldown > 0 ? `Send code (${resendCooldown}s)` : 'Send code'}
                                    </button>
                                </div>
                            }

                            {resetState &&
                                <div className="space-y-3">
                                    <div className="pt-2">
                                        <button
                                            onClick={handleSetNewPassword}
                                            type="button"
                                            className="flex w-full justify-center items-center rounded-lg bg-primary py-3 px-4 text-sm font-semibold text-white shadow-md hover:bg-secondary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-lime transition-all duration-200 uppercase tracking-wider"
                                        >
                                            Set new password
                                        </button>
                                    </div>
                                    <div className="pt-1">
                                        <button
                                            onClick={handleSubmit}
                                            type="button"
                                            disabled={resendCooldown > 0}
                                            className={`flex w-full justify-center items-center rounded-lg border py-2.5 px-4 text-sm font-semibold shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-lime focus:ring-offset-2 transition-all duration-200 ${resendCooldown > 0 ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-slate-200 hover:bg-slate-50'}`}
                                        >
                                            {resendCooldown > 0 ? `Resend Verification Code (${resendCooldown}s)` : 'Resend Verification Code'}
                                        </button>
                                    </div>
                                </div>
                            }

                            <div className="flex items-center justify-between pt-4 border-t border-slate-100 text-sm">
                                <Link to="/signin" className="font-semibold text-primary hover:text-gray-700 underline">
                                    Sign in instead
                                </Link>
                                <Link to="/signup" className="font-semibold text-primary hover:text-gray-700 underline">
                                    Create new account
                                </Link>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="text-xs text-gray-400 text-center lg:text-left space-x-2">
                    <a href="https://botcircuits.ai/privacypolicy" target="_blank" rel="noopener noreferrer" className="hover:text-gray-600 underline">
                        Privacy Policy
                    </a>
                </div>
            </div>

            <div className="hidden lg:flex bg-[#FAFAF9] relative items-center justify-center lg:h-full overflow-hidden select-none">
                {!imageFailed ? (
                    <img
                        src={authSideImage}
                        alt="Customer operations that act, not just answer."
                        className="w-full h-full object-contain object-left transition-opacity duration-300"
                        onError={() => setImageFailed(true)}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <img src={logo} alt="BotCircuits" className="h-16 w-auto" />
                        <span className="text-3xl font-extrabold text-gray-900 tracking-widest uppercase">BotCircuits</span>
                    </div>
                )}
            </div>
        </div>
    );
}

export default ForgotPasswordPage;
